import { memo } from 'react';

import { TriangleAlertIcon } from '@air/design-icons';
import { tailwindVariants } from '@air/utils-tailwind';

import { WorkspaceAgentPanelImage } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentPanelImage';
import { WORKSPACE_AGENT_PANEL_THUMBNAIL_CARD } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentPanelImageDimensions';

const workspaceAgentThumbnailCard = tailwindVariants({
  // 73px pairs with the row cell's 108px width to hold a 3:2 frame. Both come from the design spec
  // and neither is a design-system size, so both stay arbitrary.
  base: 'relative h-[73px] overflow-hidden rounded-12 ads:bg-subtle bg-gray-3',
  defaultVariants: {
    isInteractive: false,
    isUnavailable: false,
  },
  variants: {
    isInteractive: {
      true: 'block w-full text-left transition-opacity hover:opacity-90 focus-visible:outline-none ads:focus-visible:ring-focus focus-visible:ring-2 focus-visible:ring-altitude-8',
    },
    /** The panel's one error tone, matching `WorkspaceAgentUnavailablePill`. */
    isUnavailable: {
      true: 'border ads:border-error border-red-6 ads:bg-error-subtle bg-red-1',
    },
  },
});

export type WorkspaceAgentThumbnailCardProps = {
  'data-thumbnail-id'?: string;
  isUnavailable?: boolean;
  label: string;
  onOpen?: () => void;
  thumbnailUrl: string | null;
};

/**
 * A clip preview with its name burnt into the bottom edge.
 *
 * Fills its container rather than setting a width, so the caller decides between the scrolling row
 * used on desktop and the stacked full-width list used on narrow panels.
 */
export const WorkspaceAgentThumbnailCard = memo(
  ({
    'data-thumbnail-id': thumbnailId,
    isUnavailable,
    label,
    onOpen,
    thumbnailUrl,
  }: WorkspaceAgentThumbnailCardProps) => {
    let preview = <div className="size-full ads:bg-ads-gray-4 bg-gray-4" />;

    if (isUnavailable) {
      preview = (
        <div className="flex size-full items-center justify-center">
          {/* The visible label already reads "Unavailable asset", so the icon stays decorative. */}
          <TriangleAlertIcon aria-hidden className="ads:text-icon-error text-red-11" size="sm" />
        </div>
      );
    } else if (thumbnailUrl) {
      preview = (
        <WorkspaceAgentPanelImage
          {...WORKSPACE_AGENT_PANEL_THUMBNAIL_CARD}
          alt=""
          className="size-full object-cover"
          src={thumbnailUrl}
        />
      );
    }

    const content = (
      <>
        {preview}
        {isUnavailable ? (
          /* No photo to sit on, so no scrim: the label reads as the card's error tone on its red-1 ground. */
          <div className="pointer-events-none absolute inset-x-0 bottom-0 px-1.5 pb-1">
            <p className="truncate font-semibold ads:text-error text-10 text-red-11 leading-4">{label}</p>
          </div>
        ) : (
          /* No `dark:` overrides: this scrim only ever sits on a photo, so it is dark in both themes
             and a theme-aware pair would invert to dark-on-dark. */
          <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent px-1.5 pt-3 pb-1">
            <p className="truncate font-semibold text-10 text-white leading-4">{label}</p>
          </div>
        )}
      </>
    );

    if (!onOpen) {
      return (
        <div
          className={workspaceAgentThumbnailCard({ isUnavailable })}
          data-testid="WORKSPACE_AGENT_THUMBNAIL_CARD"
          data-thumbnail-id={thumbnailId}
        >
          {content}
        </div>
      );
    }

    return (
      <button
        aria-label={`Open ${label}`}
        className={workspaceAgentThumbnailCard({ isInteractive: true, isUnavailable })}
        data-testid="WORKSPACE_AGENT_THUMBNAIL_CARD"
        data-thumbnail-id={thumbnailId}
        onClick={onOpen}
        type="button"
      >
        {content}
      </button>
    );
  },
);

WorkspaceAgentThumbnailCard.displayName = 'WorkspaceAgentThumbnailCard';
